/**
 * Resolving "which work area does this string mean" — correctly **before and after** slugs become
 * unique per namespace (RA-23, RA-24).
 *
 * ⭐ **The switchover is the whole difficulty.** Today `projects.slug` is unique deployment-wide, so
 * a bare slug identifies a work area. Under R-3 (`UNIQUE (namespace_id, slug)`) it does not: `acme`
 * and `alice` may both hold `tracker`, and every stored bare slug — in `config.project`, in
 * `--work-area` in a transcript, in a saved link — becomes ambiguous on the day the constraint
 * changes. Nothing announces that day. A resolver that is correct only on one side of it is a
 * resolver that starts writing to the wrong board silently.
 *
 * ⛔ **So there is no fallback anywhere in this file**, and that is the design, not an omission.
 * Every failure is refused by name. See `resolveWorkAreaReference`.
 *
 * This is door 8 (`docs/concept-ownership-organisations-and-instances.md` §9b) one layer out from
 * the CLI, whose `resolveProject` was fixed on 2026-09-21. The shapes are deliberately the same so
 * that the CLI and the web app refuse identically.
 */
import type { IWorkAreaFormerAddress } from '../types/index.js';

/** The minimum a candidate must carry to be matched and then named in a refusal. */
export interface IWorkAreaCandidate {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  /**
   * The namespace path this work area lives in.
   *
   * ⚠️ **Optional, and its absence is load-bearing.** Before `namespace-core-lane` lands, no work
   * area has one. A qualified reference therefore matches nothing, which is the honest answer — see
   * `resolveWorkAreaReference`.
   */
  readonly namespace?: string;
  /**
   * The immutable key of the namespace this work area lives in (I-0681, ADR-113). `<key>/<slug>`
   * names the work area for as long as the namespace exists, whatever its handle becomes (AC-K8).
   */
  readonly namespaceKey?: string;
}

export interface IWorkAreaReference {
  /** The namespace half of `acme/tracker`, or `undefined` for a bare reference. */
  readonly namespace?: string;
  /** The work-area half — the whole string for a bare reference. */
  readonly workArea: string;
  /** `true` when the caller named a namespace explicitly. */
  readonly qualified: boolean;
}

/**
 * An address a work area gave up on a rename or a move, still held for it (I-1052; same rule as the
 * namespace handle hold, ADR-111/AC-M30, reused rather than rebuilt).
 *
 * `old` is a **snapshot** of the vacated identity, not a live join: the namespace itself may be
 * renamed again after this work area moved on, and the hold must keep answering to what it looked
 * like the moment it was vacated, not drift with an unrelated later rename.
 */
export interface IWorkAreaAddressHold {
  /** The vacated identity, in the same shape {@link IProject.formerAddresses} carries it in. */
  readonly old: IWorkAreaFormerAddress;
  /** The work area's current, live identity — what the old address now answers as. */
  readonly current: IWorkAreaCandidate;
}

/**
 * How long an address a work area gave up stays held for it (I-1052; same rule and the same number
 * as the namespace handle hold, {@link NAMESPACE_HANDLE_HOLD_DAYS}, ADR-111/AC-M30) — nobody else
 * can take it in that time, and the work area that gave it up can take it back.
 */
export const WORK_AREA_ADDRESS_HOLD_DAYS = 90;

export type WorkAreaResolutionType =
  | { readonly outcome: 'resolved'; readonly workArea: IWorkAreaCandidate }
  | {
      readonly outcome: 'moved';
      readonly reference: string;
      readonly workArea: IWorkAreaCandidate;
      readonly message: string;
    }
  | { readonly outcome: 'not-found'; readonly reference: string; readonly message: string }
  | {
      readonly outcome: 'ambiguous';
      readonly reference: string;
      readonly candidates: readonly IWorkAreaCandidate[];
      readonly message: string;
    };

/**
 * Splits `acme/tracker` into its halves; leaves `tracker` bare.
 *
 * ⚠️ **Only the first separator counts.** `acme/platform/api` is a namespace and a nested work-area
 * path, so everything after the first `/` is the work-area half. Splitting on the last separator
 * instead would read `acme/platform` as the namespace, which is the nesting ambiguity R-5's
 * separator exists to prevent.
 */
export function parseWorkAreaReference(reference: string): IWorkAreaReference {
  const trimmed = reference.trim().replace(/^\/+|\/+$/g, '');
  const separator = trimmed.indexOf('/');
  if (separator === -1) return { workArea: trimmed, qualified: false };
  return {
    namespace: trimmed.slice(0, separator),
    workArea: trimmed.slice(separator + 1),
    qualified: true,
  };
}

/**
 * Every candidate that answers to `reference`.
 *
 * ⛔ **All of them, never the first.** This is the same refusal the CLI's `workAreasMatching`
 * makes, and for the same reason: `find(...)` takes whichever match the listing happened to order
 * first, so two work areas colliding on any matched field resolve silently to one of them and the
 * caller then succeeds against the wrong board.
 *
 * ⚠️ **No precedence rule, deliberately.** Ranking slug above name resolves a cross-field collision
 * without asking, which is a silent winner again — better documented, no less silent. And it cannot
 * help the case that matters: two work areas with the same *name* are ambiguous under every
 * possible ranking.
 */
export function workAreasMatchingReference(
  available: readonly IWorkAreaCandidate[],
  reference: string,
): IWorkAreaCandidate[] {
  const parsed = parseWorkAreaReference(reference);

  if (parsed.qualified) {
    /*
      ⛔ A qualified reference matches namespace-and-slug **as a pair**.

      It deliberately does not fall back to matching `parsed.workArea` alone when no namespace
      matches. That fallback is the switchover defect in its purest form: before namespaces exist no
      candidate has a namespace, so `acme/tracker` would fall back and resolve to whatever `tracker`
      is — which, after the constraint changes, is somebody else's work area under a name the caller
      explicitly said was not theirs. Refusing is the only answer that is right on both sides of the
      switchover.
    */
    /*
      ⚠️ Compared as one string, `namespace/slug`, not half by half. A subgroup's full path has a
      slash of its own (`acme/platform`), so `acme/platform/api` splits at the first separator into
      `acme` and `platform/api` and would never meet a work area `api` in `acme/platform`. A slug has
      no slash, so the whole-string comparison cannot match anything the pair comparison would not.
    */
    const qualified = `${parsed.namespace}/${parsed.workArea}`;
    /*
      I-0681, AC-K8: the namespace half may be the namespace's key instead of its handle path. A key
      has no slash and names one namespace (a subgroup too, without its organisation), and the
      database keeps keys and root handles in one pool (ADR-113), so the two forms never answer for
      two different namespaces.
    */
    const asPair = available.filter(
      (candidate) =>
        (candidate.namespace !== undefined &&
          `${candidate.namespace}/${candidate.slug}` === qualified) ||
        (candidate.namespaceKey !== undefined &&
          `${candidate.namespaceKey}/${candidate.slug}` === qualified),
    );

    /*
      ⚠️ **A name may legally contain a slash, and that is not a namespace.** A work area called
      `acme/tracker` was reachable by that exact string before this function existed, and reading
      every slash as a namespace separator would make it unreachable — a legitimate reference
      refused for no reason a person can see.

      ⛔ This is a **whole-string** match, not a fallback: it drops nothing from what the caller
      typed, which is the property the paragraph above is protecting. The two sets are unioned
      rather than ranked, so a reference that is both a namespace pair and a literal name comes back
      as two candidates and is refused — genuinely ambiguous, and refused rather than guessed.
    */
    const literal = `${parsed.namespace}/${parsed.workArea}`;
    const asLiteral = available.filter(
      (candidate) => candidate.id === literal || candidate.name === literal,
    );

    return [...asPair, ...asLiteral.filter((candidate) => !asPair.includes(candidate))];
  }

  /*
    ⛔ **No own-namespace preference (I-0656, AC-M18, ruled by the dispatcher 2026-09-24).** A bare
    reference resolves only when exactly one readable work area answers to it. Agents write through
    bare slugs, and a preference for the asker's own namespace makes a reference silently start
    meaning a different work area the day a rival appears, which sends writes to the wrong board.
    Refusing is the answer that never moves.
  */
  const bare = available.filter(
    (candidate) =>
      candidate.id === parsed.workArea ||
      candidate.name === parsed.workArea ||
      candidate.slug === parsed.workArea,
  );

  return bare;
}

/**
 * Every hold whose **vacated** identity answers to `reference` (I-1052) — the same qualified/bare
 * rules `workAreasMatchingReference` applies to a live candidate, applied to `hold.old` instead.
 *
 * ⛔ **Never matched by id.** A work area's id always resolves to its live row (T-0494) and never
 * needs a hold; a hold exists only for the name or slug that changed.
 */
export function workAreaHoldsMatchingReference(
  holds: readonly IWorkAreaAddressHold[],
  reference: string,
): IWorkAreaAddressHold[] {
  const parsed = parseWorkAreaReference(reference);

  if (parsed.qualified) {
    const qualified = `${parsed.namespace}/${parsed.workArea}`;
    return holds.filter(
      (hold) =>
        (hold.old.namespace !== undefined &&
          `${hold.old.namespace}/${hold.old.slug}` === qualified) ||
        (hold.old.namespaceKey !== undefined &&
          `${hold.old.namespaceKey}/${hold.old.slug}` === qualified),
    );
  }

  return holds.filter(
    (hold) => hold.old.name === parsed.workArea || hold.old.slug === parsed.workArea,
  );
}

/**
 * The holds a list of work areas carries, flattened into `IWorkAreaAddressHold`s ready for
 * `resolveWorkAreaReference` (I-1052). Each work area's own `formerAddresses` (`IProject`'s field,
 * absent or empty once every hold on it has expired) becomes one hold per vacated address, pointing
 * at that work area as `current`.
 *
 * ⛔ **Carries the same readability filter as `available`, because it comes from the same list.** A
 * caller that has already filtered `projects` to what the reader may see needs no separate filter
 * here — a hold for an unreadable work area was never fetched to begin with.
 */
export function workAreaAddressHoldsOf<
  T extends IWorkAreaCandidate & { formerAddresses?: readonly IWorkAreaFormerAddress[] },
>(projects: readonly T[]): IWorkAreaAddressHold[] {
  return projects.flatMap((project) =>
    (project.formerAddresses ?? []).map((old) => ({ old, current: project })),
  );
}

/**
 * A work area's earlier slug, offered as a row of its own in a flat list (I-1413). A caller that
 * finds a work area by comparing each listed row's `slug` — Evorix's feedback forwarder does, and
 * cannot ask the server to resolve for it — still finds the renamed one, and the row says what it
 * is now.
 */
export interface IWorkAreaAliasEntry {
  readonly id: string;
  readonly name: string;
  /** The slug the work area used to have. */
  readonly slug: string;
  readonly namespace?: string;
  readonly namespaceKey?: string;
  /** Marks the row as an alias; its value is the work area's current `namespace/slug`. */
  readonly formerAddressOf: string;
  readonly currentSlug: string;
  readonly currentNamespace?: string;
  readonly message: string;
}

/**
 * One alias row per earlier slug of the listed work areas, for a flat list read by exact slug
 * (I-1413). Two rules keep the list from lying:
 *
 * - **An old slug a live work area holds now is not offered.** Reuse resolves to whoever holds it
 *   today, never to the work area that gave it up.
 * - **An old slug two work areas gave up is not offered at all.** A caller taking the first match
 *   would send its writes to a guess; refusing (no row) is the answer that never moves.
 *
 * Built only from `projects`, which the caller already narrowed to what the reader may read, so an
 * alias never reveals a work area the reader cannot see.
 */
export function workAreaAliasEntriesOf(
  projects: readonly (IWorkAreaCandidate & {
    formerAddresses?: readonly IWorkAreaFormerAddress[];
  })[],
): IWorkAreaAliasEntry[] {
  const liveSlugs = new Set(projects.map((project) => project.slug));
  const claims = new Map<string, IWorkAreaAliasEntry[]>();
  for (const project of projects) {
    const current = workAreaQualifiedAddress(project);
    for (const old of project.formerAddresses ?? []) {
      if (liveSlugs.has(old.slug)) continue;
      const entry: IWorkAreaAliasEntry = {
        id: project.id,
        name: project.name,
        slug: old.slug,
        namespace: old.namespace,
        namespaceKey: old.namespaceKey,
        formerAddressOf: current,
        currentSlug: project.slug,
        currentNamespace: project.namespace,
        message: `"${old.slug}" is now "${current}"; address it by that, or by id ${project.id}.`,
      };
      claims.set(old.slug, [...(claims.get(old.slug) ?? []), entry]);
    }
  }
  return [...claims.values()].flatMap((entries) =>
    new Set(entries.map((entry) => entry.id)).size === 1 ? [entries[0]] : [],
  );
}

/**
 * Names every candidate with the id that would disambiguate it.
 *
 * The id is what it recommends, not the slug: when the collision is one area's slug against
 * another's name — or, after R-3, one namespace's `tracker` against another's — repeating the slug
 * lands straight back here.
 */
export function ambiguousWorkAreaReferenceMessage(
  reference: string,
  candidates: readonly IWorkAreaCandidate[],
): string {
  const qualified = candidates.every((candidate) => candidate.namespace !== undefined);
  return [
    `Work area "${reference}" is ambiguous: ${candidates.length} work areas answer to it.`,
    ...candidates.map((candidate) => {
      const where =
        candidate.namespace === undefined
          ? candidate.slug
          : `${candidate.namespace}/${candidate.slug}`;
      return `  ${candidate.name} — ${where}, id ${candidate.id}`;
    }),
    'A work area is matched by id, name or slug alike, so that does not say which.',
    qualified ? 'Name it by its namespace and slug, or by id:' : 'Name it by id, which is unique:',
    ...candidates.map((candidate) =>
      candidate.namespace === undefined
        ? `  ${candidate.id}`
        : `  ${candidate.namespace}/${candidate.slug}`,
    ),
  ].join('\n');
}

/**
 * Resolve, or refuse by name.
 *
 * ⛔ **No "best guess" and no "first match".** A caller that cannot be told which work area was
 * meant is told that, with the candidates, so the fix is a copy-paste. This fleet writes through
 * the CLI and the web app, and a first-match resolver is the one failure shape that does not
 * announce itself — it succeeds, prints a work area, and changes a different board.
 *
 * ⭐ **A fourth outcome, added for I-1052.** A reference with no live match is checked against
 * `holds` — addresses recently vacated by a rename or a move (same rule as the namespace handle
 * hold) — before it is refused. `holds` defaults to none, so a caller that has not fetched any
 * resolves exactly as before. ⛔ **The caller filters `holds` to what the reader may read, the same
 * way it filters `available`** (I-0656, AC-M22's rule, unchanged): a hold for a work area the reader
 * may not see must never be passed in, or the old address would answer "moved" for something the new
 * address would refuse to name at all — the one leak AC-M18's privacy design exists to prevent.
 */
export function resolveWorkAreaReference(
  available: readonly IWorkAreaCandidate[],
  reference: string,
  holds: readonly IWorkAreaAddressHold[] = [],
): WorkAreaResolutionType {
  const matches = workAreasMatchingReference(available, reference);

  if (matches.length === 1) return { outcome: 'resolved', workArea: matches[0] };

  if (matches.length === 0) {
    const held = workAreaHoldsMatchingReference(holds, reference);
    // Deduplicated by id, not by object identity: several holds (a chain of renames) legitimately
    // point at the same live row as separate, independently built `current` objects.
    const currents = [...new Map(held.map((hold) => [hold.current.id, hold.current])).values()];
    if (currents.length === 1) {
      const workArea = currents[0];
      const to = workAreaQualifiedAddress(workArea);
      return {
        outcome: 'moved',
        reference,
        workArea,
        message: `"${reference}" is now "${to}".`,
      };
    }
    if (currents.length > 1) {
      return {
        outcome: 'ambiguous',
        reference,
        candidates: currents,
        message: ambiguousWorkAreaReferenceMessage(reference, currents),
      };
    }

    const parsed = parseWorkAreaReference(reference);
    const message = parsed.qualified
      ? `Work area "${reference}" was not found. No namespace "${parsed.namespace}" holds a work area "${parsed.workArea}".`
      : `Work area "${reference}" was not found.`;
    return { outcome: 'not-found', reference, message };
  }

  return {
    outcome: 'ambiguous',
    reference,
    candidates: matches,
    message: ambiguousWorkAreaReferenceMessage(reference, matches),
  };
}

/** `namespace/slug`, or the bare slug when no namespace is known — the form a "moved" message names. */
function workAreaQualifiedAddress(workArea: IWorkAreaCandidate): string {
  return workArea.namespace === undefined
    ? workArea.slug
    : `${workArea.namespace}/${workArea.slug}`;
}
